// ─── Services d'ADMINISTRATION (comptes & forfaits) ───────────────
// Réservé à l'app mop-admin. Les écritures ne passent que si le JETON porte le
// claim `admin` (ou `commerce` selon la règle) : l'UI n'est qu'un confort, la
// sécurité vit dans firestore.rules, à la RACINE de ce dépôt (rapatrié le 08/08 ;
// la copie de mop-cal-v3 est périmée, ne plus déployer depuis là-bas).
//   users/{uid}          : plan, statut, rôles, achats à la carte
//   config/fonctionnalites : { globales, plans, paliers } — moteur de forfaits
import { db, functions } from '@mop/core';
import {
  collection, doc, getDoc, onSnapshot, query, limit, setDoc, updateDoc, deleteField,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export { PLANS, STATUTS } from '@mop/core';

// Tous les comptes (les règles n'autorisent la lecture qu'à un admin).
//
// ⚠️ PAS de `orderBy('creeLe')` ici, et c'est délibéré : Firestore EXCLUT d'un
// résultat trié tout document qui ne porte pas le champ de tri. Or `creeLe`
// n'est posé que par `AuthContext` À LA CRÉATION du profil — les comptes
// hérités de MoPCal en sont dépourvus et disparaissaient donc de TOUTE l'app
// (liste des comptes, écran Accès, feuille de choix), sans le moindre message.
// Vécu le 09/08 : 6 comptes affichés sur 8, deux comptes bien réels impossibles
// à administrer et étiquetés « Pas encore inscrit ».
// Un tri en mémoire est ici gratuit (plafond de 500 documents) et, surtout, il
// ne peut pas faire disparaître une ligne. Même patron que `espaces.js` :
// `String()` défensif, car un `creeLe` non-chaîne (Timestamp) ferait planter
// `localeCompare`. Les comptes sans date ferment la liste plutôt que d'en sortir.
export function ecouterComptes(callback, onErreur, max = 500) {
  return onSnapshot(
    query(collection(db, 'users'), limit(max)),
    (snap) => callback(snap.docs
      .map(d => ({ uid: d.id, ...d.data() }))
      .sort((a, b) => String(b.creeLe || '').localeCompare(String(a.creeLe || '')))),
    (e) => { callback([]); onErreur?.(e); },
  );
}

// Champs administrables d'un compte (plan, statut, rôles).
export async function majCompte(uid, champs) {
  await updateDoc(doc(db, 'users', uid), champs);
}

// Achat « à la carte » d'une fonction : { [cle]: null | 'AAAA-MM-JJ' (fin) }.
export async function definirAchat(uid, cle, fin) {
  await setDoc(doc(db, 'users', uid), { fonctionnalitesAchetees: { [cle]: fin || null } }, { merge: true });
}
export async function retirerAchat(uid, cle, achatsActuels) {
  const a = { ...(achatsActuels || {}) };
  delete a[cle];
  await setDoc(doc(db, 'users', uid), { fonctionnalitesAchetees: a }, { merge: true });
}

// ── Configuration des forfaits (doc unique, lu par le moteur du Core) ──
export function ecouterConfigForfaits(callback, onErreur) {
  return onSnapshot(doc(db, 'config', 'fonctionnalites'),
    (snap) => {
      const d = snap.exists() ? snap.data() : {};
      callback({ globales: d.globales || {}, plans: d.plans || {}, paliers: d.paliers || {}, poids: d.poids || {} });
    },
    (e) => { callback({ globales: {}, plans: {}, paliers: {}, poids: {} }); onErreur?.(e); });
}
// Désactivation GLOBALE d'une fonction (porte 1 du moteur).
export const definirGlobale = (cle, actif) =>
  setDoc(doc(db, 'config', 'fonctionnalites'), { globales: { [cle]: actif } }, { merge: true });
// Palier inclus par un plan (porte 2) + palier forcé d'une fonction.
export const definirPalierPlan = (plan, palierInclus) =>
  setDoc(doc(db, 'config', 'fonctionnalites'), { plans: { [plan]: { palierInclus } } }, { merge: true });
export const definirPalierFonction = (cle, palier) =>
  setDoc(doc(db, 'config', 'fonctionnalites'), { paliers: { [cle]: palier } }, { merge: true });
// Jetons d'ÉLÉMENTS gérables accordés par un plan (⚙️ gestion / 👁 consultation).
export const definirJetonsPlan = (plan, nature, n) =>
  setDoc(doc(db, 'config', 'fonctionnalites'),
    { plans: { [plan]: { jetons: { [nature]: Math.max(0, Number(n) || 0) } } } }, { merge: true });

// Nombre d'ESPACES qu'un plan autorise (au moins 1 : sans espace, un compte ne
// peut rien ranger — descendre a 0 le rendrait inutilisable).
export const definirEspacesPlan = (plan, n) =>
  setDoc(doc(db, 'config', 'fonctionnalites'),
    { plans: { [plan]: { maxEspaces: Math.max(1, Number(n) || 1) } } }, { merge: true });

// Plafond PAR TYPE d'élément (calendriers, inventaires/plans, repas, comptes) —
// distinct des jetons (pool commun). `n === null` pose l'illimité.
export const definirMaxType = (plan, type, n) =>
  setDoc(doc(db, 'config', 'fonctionnalites'),
    { plans: { [plan]: { maxParType: { [type]: n === null ? null : Math.max(0, Number(n) || 0) } } } }, { merge: true });

// Budget de points OFFERTS par un plan, achat à la carte (19/08). 0 minimum
// (pas de budget négatif) — pas de plancher à 1 comme les espaces : un plan
// sans pool est un choix commercial valide (Gratuit, typiquement).
export const definirPointsInclus = (plan, n) =>
  setDoc(doc(db, 'config', 'fonctionnalites'),
    { plans: { [plan]: { pointsInclus: Math.max(0, Number(n) || 0) } } }, { merge: true });

// Poids (en points) d'une fonction individuelle dans le pool à la carte.
// Minimum 1 : un poids à 0 rendrait la fonction gratuite du pool, jamais
// l'intention d'un réglage à la baisse.
export const definirPoidsFonction = (cle, poids) =>
  setDoc(doc(db, 'config', 'fonctionnalites'), { poids: { [cle]: Math.max(1, Number(poids) || 1) } }, { merge: true });

// ── Barème (doc `config/pointsALaCarte`, DISTINCT des deux docs ci-dessus) —
//    prix mensuel au-delà du budget inclus, par palier de points. Le tableau
//    entier est remplacé (Firestore ne fusionne pas un array élément par
//    élément) : l'appelant relit `ecouterBareme` avant d'éditer.
export const definirBareme = (bandes) =>
  setDoc(doc(db, 'config', 'pointsALaCarte'), { bareme: bandes }, { merge: true });

// ── Demande d'achat à la carte déposée par l'utilisateur ────────────
// ADDITIF SEULEMENT : accepter ajoute aux achats existants, ne retire jamais
// rien (retirer un achat individuel reste `retirerAchat`, ci-dessus, à
// brancher plus tard si besoin — hors scope de ce premier incrément).
export async function accepterDemandeAchats(compte) {
  const d = compte?.achatsDemande || {};
  const achats = { ...(compte.fonctionnalitesAchetees || {}) };
  (d.fonctions || []).forEach(cle => { achats[cle] = null; });   // null = à vie
  const quotas = { ...(compte.quotasAchetes || {}) };
  Object.entries(d.quotas || {}).forEach(([cle, n]) => {
    quotas[cle] = (Number(quotas[cle]) || 0) + (Number(n) || 0);
  });
  await setDoc(doc(db, 'users', compte.uid), {
    fonctionnalitesAchetees: achats, quotasAchetes: quotas,
    achatsDemande: deleteField(), achatsDemandeLe: deleteField(),
  }, { merge: true });
}
export async function refuserDemandeAchats(uid) {
  await setDoc(doc(db, 'users', uid), { achatsDemande: deleteField(), achatsDemandeLe: deleteField() }, { merge: true });
}

// ── RÔLES : écriture réelle + application des CLAIMS ──────────────
// Sources des rôles (fixées par les règles et la Cloud Function) :
//   • admin / commerce → listes d'e-mails dans `config/admin` (.emails / .commerce)
//   • ivv              → `users/{uid}.ivv`
// Après écriture, on appelle la callable `appliquerClaims` (europe-west9,
// réservée aux admins) qui pose les custom claims sur le JETON : sans cet
// appel, le rôle est affiché mais ne donne AUCUN droit en base.
const majListe = (liste, email, present) => {
  const bas = String(email || '').toLowerCase();
  const set = new Set((liste || []).map(x => String(x).toLowerCase()));
  if (present) set.add(bas); else set.delete(bas);
  return [...set];
};

export async function definirRoles(uid, email, { admin, commerce, ivv }, monEmail) {
  // 1. IVV — porté par le profil utilisateur.
  await setDoc(doc(db, 'users', uid), { ivv: !!ivv, admin: !!admin, commerce: !!commerce }, { merge: true });
  // 2. admin / commerce — listes d'e-mails de config/admin. Garde-fou des
  //    règles : l'auteur doit RESTER dans `emails` (anti-lockout).
  if (email) {
    const snap = await getDoc(doc(db, 'config', 'admin'));
    const cfg = snap.exists() ? snap.data() : {};
    const emails = majListe(cfg.emails, email, admin);
    if (monEmail && !emails.map(x => x.toLowerCase()).includes(String(monEmail).toLowerCase())) {
      emails.push(String(monEmail).toLowerCase());          // ne jamais se verrouiller dehors
    }
    await setDoc(doc(db, 'config', 'admin'), {
      emails, commerce: majListe(cfg.commerce, email, commerce),
    }, { merge: true });
  }
  // 3. Claims (le jeton fait foi pour les règles).
  return httpsCallable(functions, 'appliquerClaims')({ uid });
}

// ── Plans & tarifs (doc `config/plans`, DISTINCT de `config/fonctionnalites`
//    ci-dessus) : nom affiché, prix, limites d'usage. Fusion, jamais une
//    réécriture complète — un forfait créé depuis l'administration et pas
//    encore connu du code doit rester lisible par les autres champs déjà posés.
export const definirPlan = (planId, patch) =>
  setDoc(doc(db, 'config', 'plans'), { [planId]: patch }, { merge: true });

// ── Demande de forfait déposée par l'utilisateur (section « Mon forfait ») ──
export async function appliquerDemandeForfait(uid, plan) {
  await setDoc(doc(db, 'users', uid), {
    plan, planDemande: deleteField(), planDemandeLe: deleteField(),
  }, { merge: true });
}
export async function refuserDemandeForfait(uid) {
  await setDoc(doc(db, 'users', uid), { planDemande: deleteField(), planDemandeLe: deleteField() }, { merge: true });
}
